import type { ChatToolCall } from '../api/domains/chatbot.api'

/**
 * Assistant tools that change Security state on the server. When a finished turn carries a
 * successful one, the /security pages re-read their targets and checks: `apps/console` has no
 * query cache, so this module-level signal stands in for it (the same shape as forms-refresh.ts).
 */
export const SECURITY_WRITE_TOOLS: ReadonlySet<string> = new Set(['run_security_scan'])

/** A scan an assistant tool started or finished; `status` is the tool's word ('' when it gave none). */
export interface SecurityRun {
  targetId: string
  runId: string
  status: string
}

export interface SecurityChange {
  targetIds: string[]
  runs: SecurityRun[]
}

type Listener = (change: SecurityChange) => void

const listeners = new Set<Listener>()

export function subscribeSecurityRefresh(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export function announceSecurityWrites(toolCalls: readonly ChatToolCall[]): void {
  const targetIds: string[] = []
  const runs: SecurityRun[] = []

  for (const call of toolCalls) {
    if (!SECURITY_WRITE_TOOLS.has(call.name)) continue
    const r = call.result as { success?: unknown; target_id?: unknown; run_id?: unknown; status?: unknown } | null

    if (r && typeof r === 'object' && r.success === true && typeof r.target_id === 'string' && r.target_id) {
      targetIds.push(r.target_id)

      if (typeof r.run_id === 'string' && r.run_id) {
        runs.push({ targetId: r.target_id, runId: r.run_id, status: typeof r.status === 'string' ? r.status : '' })
      }
    }
  }

  if (targetIds.length === 0) return

  for (const listener of [...listeners]) {
    try {
      listener({ targetIds, runs })
    } catch {
      // One page's failed refresh must not stop the others.
    }
  }
}
